/**
 * Browser fingerprinting utility for legal compliance and user identification
 * Collects necessary data when users sign contracts
 */

export interface BrowserFingerprint {
  ipAddress?: string // Will be set by backend from request
  userAgent: string
  acceptLanguage: string
  referer: string
  screenResolution: string
  timezone: string
  browserFingerprint: string
  sessionId: string
  geoCountry?: string // Can be determined by backend from IP
  geoCity?: string // Can be determined by backend from IP
}

/**
 * Generates a simple browser fingerprint based on available browser features
 */
export const generateBrowserFingerprint = (): string => {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  if (ctx) {
    ctx.textBaseline = 'top'
    ctx.font = '14px Arial'
    ctx.fillText('Browser fingerprint test', 2, 2)
  }

  const canvasFingerprint = canvas.toDataURL()

  const fingerprint = [
    navigator.userAgent,
    navigator.language,
    window.screen.width + 'x' + window.screen.height,
    window.screen.colorDepth,
    new Date().getTimezoneOffset(),
    !!window.sessionStorage,
    !!window.localStorage,
    navigator.hardwareConcurrency || 'unknown',
    canvasFingerprint.slice(-50), // Last 50 chars of canvas fingerprint
  ].join('|')

  // Create a simple hash
  let hash = 0
  for (let i = 0; i < fingerprint.length; i++) {
    const char = fingerprint.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash // Convert to 32bit integer
  }

  return Math.abs(hash).toString(36)
}

/**
 * Generates a session ID for tracking the current browser session
 */
export const generateSessionId = (): string => {
  const timestamp = Date.now().toString(36)
  const randomPart = Math.random().toString(36).substr(2, 9)
  return `${timestamp}-${randomPart}`
}

/**
 * Gets the current session ID from sessionStorage or creates a new one
 */
export const getOrCreateSessionId = (): string => {
  const existingSessionId = sessionStorage.getItem('clip_session_id')

  if (existingSessionId) {
    return existingSessionId
  }

  const newSessionId = generateSessionId()
  sessionStorage.setItem('clip_session_id', newSessionId)
  return newSessionId
}

/**
 * Gets a short timezone representation (max 10 characters)
 * Examples: "+03:00", "-05:00", "Z" (for UTC)
 */
const getShortTimezone = (): string => {
  try {
    const now = new Date()
    const offset = now.getTimezoneOffset() // Minutes difference from UTC

    if (offset === 0) return 'Z' // UTC

    const hours = Math.floor(Math.abs(offset) / 60)
    const minutes = Math.abs(offset) % 60
    const sign = offset > 0 ? '-' : '+' // Note: getTimezoneOffset returns positive for west of UTC

    return `${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
  } catch (error) {
    return 'UTC'
  }
}

/**
 * Collects all available browser information for contract signing
 */
export const collectBrowserFingerprint = (): Omit<
  BrowserFingerprint,
  'ipAddress' | 'geoCountry' | 'geoCity'
> => {
  return {
    userAgent: navigator.userAgent,
    acceptLanguage: navigator.language || navigator.languages?.[0] || 'en',
    referer: document.referrer || window.location.origin,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    timezone: getShortTimezone(),
    browserFingerprint: generateBrowserFingerprint(),
    sessionId: getOrCreateSessionId(),
  }
}

/**
 * Collects minimal browser data for contract signing (fallback for older browsers)
 */
export const collectMinimalBrowserData = (): Partial<BrowserFingerprint> => {
  try {
    return collectBrowserFingerprint()
  } catch (error) {
    console.warn(
      'Failed to collect full browser fingerprint, using minimal data:',
      error,
    )

    return {
      userAgent: navigator.userAgent || 'Unknown',
      acceptLanguage: navigator.language || 'en',
      referer: document.referrer || '',
      screenResolution:
        window.screen.width && window.screen.height
          ? `${window.screen.width}x${window.screen.height}`
          : 'unknown',
      timezone: 'UTC',
      browserFingerprint: 'minimal',
      sessionId: getOrCreateSessionId(),
    }
  }
}
